<script>
    import axios from 'axios';
    import { setCookie, timeHandler, readCookie } from '../../../script/common';
    import { carrierService } from '../../../service/resource';
    import customParseFormat from 'dayjs/plugin/customParseFormat';
    import store from '../../../script/store/checkout';
    import { CHECKOUT_STEPS } from '../../../script/store/checkout/consts';

    timeHandler.extend(customParseFormat);

    const CancelToken = axios.CancelToken;

    export default {
        name: 'BaseCheckoutCarrier',

        props: {
            summarize: Boolean,
        },

        data() {
            return {
                ...CHECKOUT_STEPS,

                storeState: store.state,

                enableExpress: false,
                express: {
                    days: 0,
                    price: 0,
                    except: '',
                },

                loading: false,

                selectedCarrier: null,
                selectedSchool: null,
                quotation: null,

                changeSchoolBranch: false,
                actualBranchSchoolId: null,
                selectedBranchSchool: readCookie('branch_school_id'),

                ownError: null,

                hasFastDelivery: !!readCookie('_fast-delivery-cep'),
            };
        },

        watch: {
            activeStep: {
                immediate: true,
                handler(to, from) {
                    if (to === this.STEP_CARRIER && (!from || from < to)) {
                        this.fetchCarriers();
                    } else if (to < this.STEP_CARRIER) {
                        this.cancelLoadRequest();
                    }
                },
            },

            cart: {
                immediate: true,
                handler(to) {
                    if (
                        to &&
                        this.activeStep >= this.STEP_CARRIER &&
                        !to.is_only_virtual_products
                    ) {
                        this.fetchCarriers();
                    }
                },
            },
        },

        computed: {
            cart() {
                return this.storeState.cart.cart;
            },

            activeStep() {
                return this.storeState.step.active;
            },

            storeError() {
                if (this.storeState.carrier.error) {
                    return this.storeState.carrier.error.i18n
                        ? this.$t(this.storeState.carrier.error.msg)
                        : this.storeState.carrier.error.msg;
                } else {
                    return null;
                }
            },

            alert() {
                return this.ownError || this.storeError
                    ? {
                          message: this.ownError || this.storeError,
                          isError: true,
                          timeout: 0,
                      }
                    : null;
            },

            fastLoginEmail() {
                return this.storeState.authentication.fastLoginEmail;
            },

            showDeliveryFastMessage() {
                return (
                    this.hasFastDelivery &&
                    this.selectedCarrier &&
                    timeHandler(this.selectedCarrier.actual_delivery_forecast).isAfter(
                        timeHandler('2024-12-23').endOf('day')
                    )
                );
            },
        },

        methods: {
            cancelLoadRequest() {
                if (this.carrierCancel) {
                    this.carrierCancel.cancel();
                    this.carrierCancel = undefined;
                }
            },

            clickChangeSchoolBranch(actualBranchSchoolId) {
                this.actualBranchSchoolId = actualBranchSchoolId;
                this.changeSchoolBranch = true;
            },

            selectBranchSchool(branchSchoolId) {
                this.selectedBranchSchool = branchSchoolId;
                this.changeSchoolBranch = false;
                this.fetchCarriers();
            },

            fetchCarriers() {
                this.loading = true;

                this.cancelLoadRequest();

                this.ownError = null;
                this.quotation = undefined;
                this.cart.address.data.country_id = this.cart.address.data.country_id
                    ? this.cart.address.data.country_id
                    : 245;
                setCookie('country_id', this.cart.address.data.country_id);

                this.carrierCancel = CancelToken.source();

                carrierService
                    .query(
                        {
                            cart: this.cart.id,
                            cep: this.cart.address.data.zip_code.includes('*')
                                ? null
                                : this.cart.address.data.zip_code,
                            school_branch_id: this.selectedBranchSchool,
                            pais: this.cart.address.data.country_id,
                        },
                        {
                            headers: {
                                'cr-api-fast-auth': this.fastLoginEmail,
                            },
                            cancelToken: this.carrierCancel.token,
                        }
                    )
                    .then((response) => {
                        let quotation = response.data.data;

                        if (!quotation || !quotation.responses.length) {
                            this.ownError = this.$t('_address.zipCodeNotFoundOrUnavailable');
                            return;
                        }

                        if (
                            quotation.cep_validation !==
                                this.cart.address.data.zip_code_validation &&
                            this.cart.address.data.country_id === 245
                        )
                            throw new axios.Cancel();

                        window.dataLayer.push({
                            event: 'listCarrierCheckout',
                            ecommerce: {
                                checkout: {
                                    actionField: { step: 2 },
                                },
                            },
                        });
                        // Habilita produção expressa caso o cliente tenha selecionado anteriormente no carrinho
                        if (!this.enableExpress && parseInt(readCookie('express')) === 1) {
                            this.toggleExpressShipping();
                        }

                        this.updateCarriersList(quotation);
                    })
                    .catch((error) => {
                        if (axios.isCancel(error)) {
                            return;
                        }
                        this.ownError = error.response
                            ? error.response.data.message
                            : this.$t('_address.errorCalculatingShippingTryAgain');
                    })
                    .then(() => (this.loading = false));
            },

            getFormattedInitialForecast(carrier) {
                return this.$f
                    .formatDate(carrier.actual_delivery_forecast_initial, this.$t('_date.ddOfMmmm'))
                    .toUpperCase();
            },

            getFormattedFinalForecast(carrier) {
                return this.$f.formatDate(
                    carrier.actual_delivery_forecast,
                    this.$t('_date.ddOfMmmm')
                );
            },

            getIsoFormattedFinalForecast(carrier) {
                let date = new Date(carrier.delivery_forecast_final);
                let month = date.getMonth() + 1;
                let dt = date.getDate();
                return dt + '/' + month;
            },

            convertIsoToDate(carrier) {
                let date = new Date(carrier);
                let month = date.getMonth() + 1;
                let dt = date.getDate();

                return 'Entrega até ' + dt + '/' + month;
            },

            updateCarriersList(quotation) {
                let express = { days: 0, price: 0, except: '' };

                const expressArray = [];
                // Verifica se possui opção de frete e pega quantos dias adianta
                for (const carrier of quotation.responses) {
                    if (carrier.express !== undefined) {
                        this.enableExpress = true;
                        express.price = carrier.express.additional_price;
                        if (express.days < carrier.express.days_before)
                            express.days = carrier.express.days_before;
                    } else {
                        expressArray.push(carrier.prime ? 'Prime' : carrier.name);
                    }
                }

                express.except = expressArray.join(', ');

                if (!this.enableExpress) {
                    this.cart.expresso = false;
                }

                for (const carrier of quotation.responses) {
                    carrier.haveExpress = carrier.express !== undefined;
                    carrier.expressEnabled = this.cart.expresso ? carrier.haveExpress : false;

                    carrier.actual_additional_price =
                        this.cart.expresso && carrier.expressEnabled
                            ? carrier.express.additional_price
                            : 0;
                    carrier.actual_price = carrier.price + carrier.actual_additional_price;
                    carrier.actual_delivery_forecast_initial =
                        this.cart.expresso && carrier.expressEnabled
                            ? carrier.express.delivery_forecast_initial
                            : carrier.delivery_forecast_initial;
                    carrier.actual_delivery_forecast =
                        this.cart.expresso && carrier.expressEnabled
                            ? carrier.express.delivery_forecast_final
                            : carrier.delivery_forecast_final;

                    if (carrier.available_schools_branches) {
                        quotation.available_schools_branches = carrier.available_schools_branches;
                    }
                }

                this.express = express;
                this.quotation = quotation;

                this.selectedCarrier = null;

                if (this.cart.carrier_id) {
                    const cartCarrier = this.quotation.responses.find(
                        (response) =>
                            response.carrier_id === this.cart.carrier_id &&
                            (this.cart.frete_id === undefined ||
                                response.frete_id === this.cart.frete_id)
                    );

                    if (cartCarrier) {
                        this.selectCarrier(cartCarrier);
                    }
                }

                if (!this.selectedCarrier) {
                    this.selectCarrier(this.quotation.responses[0]);
                }
            },

            toggleExpressShipping() {
                this.cart.expresso = !this.cart.expresso;
                setCookie('express', this.cart.expresso ? 1 : 0);
                if (this.quotation) this.updateCarriersList(this.quotation);
            },

            selectCarrier(carrier) {
                store.step.goTo(this.STEP_CARRIER);

                this.selectedCarrier = carrier;

                this.initialForecast = this.selectedCarrier.actual_delivery_forecast_initial;
                this.finalForecast = this.selectedCarrier.actual_delivery_forecast;

                window.dataLayer.push({
                    event: 'add_shipping_info',
                    value: this.selectedCarrier.price,
                    currency: 'BRL',
                    shipping_tier: this.selectedCarrier.name,
                });

                store.carrier.select(this.quotation, this.selectedCarrier);

                if (carrier.carrier_id === 81027) {
                    this.$emit('pickup-store', this.selectedCarrier.store);
                }
            },
        },
    };
</script>
